import { FC, useCallback, useMemo, useState } from 'react';
import { EmptyStateIcons, EmptyStateProps, Table } from '@silverfort/sf-components';
import { LOCAL_ACCOUNTS_TABLE_COLUMNS, LOCAL_ACCOUNTS_TABLE_SORT_VALUES } from './consts';
import { LocalAccountRow } from './types';
import { useLocalAccounts } from '../../context/LocalAccountsContext';
import LocalAccountsBulkActions from './LocalAccountsBulkActions';

export type LocalAccountsTableProps = {
    height?: number;
};

const keyExtractor = (row: LocalAccountRow) => row.guid;

const LocalAccountsTable: FC<LocalAccountsTableProps> = ({ height }) => {
    const {
        localAccounts,
        fetchMore,
        error,
        loading,
        hasMore,
        mutate: mutateLocalAccounts,
        setOrder
    } = useLocalAccounts();
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

    const onSelectRows = useCallback((selectedRowsIds: string[]) => {
        setSelectedAccounts(selectedRowsIds);
    }, []);

    const handleScrollEnd = useCallback(() => {
        if (hasMore) {
            fetchMore();
        }
    }, [hasMore, fetchMore]);

    const handleCloseBulkActions = useCallback(() => {
        setSelectedAccounts([]);
    }, []);

    const handleSortChange = useCallback(
        (columnKey: string, sortDirection: 'asc' | 'desc') => {
            setOrder({ orderBy: LOCAL_ACCOUNTS_TABLE_SORT_VALUES[columnKey], orderDirection: sortDirection });
        },
        [setOrder]
    );

    const localAccountsRows = useMemo(
        () => localAccounts.map((account) => ({ ...account, manage: undefined as never })),
        [localAccounts]
    );

    const selectedLocalAccounts = useMemo(
        () => localAccounts.filter((account) => selectedAccounts.includes(account.guid)),
        [localAccounts, selectedAccounts]
    );

    const emptyStateProps: EmptyStateProps = useMemo(() => {
        if (error) {
            return {
                title: 'Error',
                description: (
                    <span>
                        Something went wrong. Try again. If error persists,
                        <br />
                        contact your Silverfort representative.
                    </span>
                ),
                icon: EmptyStateIcons.Error,
                className: 'py-6',
                contentClassName: 'pb-6'
            };
        }

        return {
            title: 'No results',
            description: 'Try refining your search',
            icon: EmptyStateIcons.NoResults,
            className: 'pt-6 pb-12'
        };
    }, [error]);

    return (
        <>
            <Table
                columns={LOCAL_ACCOUNTS_TABLE_COLUMNS}
                rows={localAccountsRows}
                keyExtractor={keyExtractor}
                selectable
                selected={selectedAccounts}
                onSelect={onSelectRows}
                bodyHeight={height}
                onScrollEnd={handleScrollEnd}
                tableContainerClassName="h-full w-full overflow-hidden"
                rowClassName="last:mb-4"
                EmptyStateProps={emptyStateProps}
                loading={loading}
                onSortChange={handleSortChange}
            />
            <LocalAccountsBulkActions
                selectedAccounts={selectedLocalAccounts}
                onClose={handleCloseBulkActions}
                onActionSuccess={mutateLocalAccounts}
            />
        </>
    );
};

export default LocalAccountsTable;
